import axios from 'axios'
import { isNetworkFailure, isReauthenticationRequired } from './passkey'

/**
 * 管理者が、社員の2要素の登録状況を見て、必要なら外す。
 *
 * <h3>なぜ要るのか</h3>
 *
 * 2026-08-22 まで、**2要素を失った人を誰も元に戻せなかった**。
 * 解除の口は本人用の1本きりで、締め出された人はそもそも呼べない。
 * 認証アプリの端末と予備のコード10本の両方を失うと、**本番のDBを
 * 直接触るしか復旧手段が無かった**。
 *
 * <h3>誰の会社かは送らない</h3>
 *
 * **会社はサーバがセッションから決める。** 画面から送る形にすると、
 * その値を書き換えるだけで他社の一覧が読める。ここで送るのは
 * 「その会社の中の誰か」だけ。
 */

const BASE = process.env.VUE_APP_API_ORIGIN

/**
 * 会社の利用者ごとの、2要素の登録状況。
 *
 * **秘密は返ってこない。** 共有鍵も、予備のコードも、残り本数も。
 * 分かるのは「登録しているか」だけ。
 *
 * @returns {Promise<{users: Array, total: number, notEnrolled: number}>}
 */
export async function getEnrollmentStatus () {
  const response = await axios.get(BASE + '/tenant-access-management/mfa-enrollment')
  const data = response.data || {}
  return {
    users: Array.isArray(data.users) ? data.users : [],
    total: Number(data.total || 0),
    notEnrolled: Number(data.notEnrolled || 0),
    /*
     * **必須にして実際に止まるのは管理者だけ。**ここを写し忘れると、
     * 画面は 0 として読み、「管理者は全員が登録済みです」と出す。
     * 通信は成功したまま、中身だけが空になる形。
     */
    adminsNotEnrolled: Number(data.adminsNotEnrolled || 0),
    /*
     * いまの段。**読めなかった会社では、サーバがこの欄を返さない。**
     * 空のまま渡す。ここで NONE に倒すと、分からないことを
     * 「必須ではありません」と言い換えたことになる。
     */
    stage: data.stage || '',
    requiredFrom: data.requiredFrom || null
  }
}

/**
 * その人の2要素を外す。
 *
 * 外された人は、次のログインで会社の方針に従って登録の段へ送られる。
 * **外して終わりではない。**
 *
 * <b>ログインから15分を過ぎていると断られる。</b>他人の認証を触るので、
 * サーバが本人確認のやり直しを求める。
 *
 * @param {number} userId 外される人
 * @param {string} reason 台帳へ残す短い理由。空でもよい
 */
export async function resetMfaFor (userId, reason) {
  await axios.delete(
    BASE + '/tenant-access-management/users/' + encodeURIComponent(userId) + '/mfa',
    { data: { reason: reason || null } })
}

/*
 * **一時パスを出す口は、ここには置かない（場面 S-95）。**
 *
 * ここは<b>顧客企業の管理者の道</b>。2要素を<b>外す</b>のはそれで
 * よい——外された人は次のログインで自分で登録し直せる。
 *
 * 一時パスを<b>出す</b>のは違う。あれは<b>その人になりすませる
 * 文字列</b>で、第2要素を通らずに登録をやり直せる。ここに置くと
 * <b>会社側の管理者が、自分の第2要素を迂回する資格情報を自分で
 * 作れる。</b>
 *
 * 出す口は運営側（services/accessManagement.js）。
 */
/**
 * サーバが返した文言。無ければ null。
 *
 * **自分で作った文言で上書きしない。** サーバは「自分で自分は外せません」
 * 「その利用者が見つかりません」を区別して返す。こちらで1種類にまとめると、
 * 押した人は何を直せばよいか分からない。
 */
export function serverMessage (error) {
  const response = error && error.response
  const data = (response && response.data) || {}
  const message = data.message
  return typeof message === 'string' && message ? message : null
}

/*
 * 通信断と本人確認のやり直しの見分け方は、1か所しか持たない。
 * 同じ判定を写すと、サーバが印を変えたときに片方だけ直る。
 */
export { isNetworkFailure, isReauthenticationRequired }
